/**
 * @file useSpiffProgram.js
 * @description Data hook for the SPIFF Program Workspace (C-66).
 *
 * S924 — Runtime instrument surface hook.
 * Mirrors usePlanDocument.js pattern: native fetch, AbortController, mounted ref.
 * Does NOT use usePlanDocument, usePlanGovernance, or usePlanValidation —
 * those are design-time plan hooks. This is a standalone runtime instrument hook.
 *
 * Owns:
 *   GET  /api/v1/spiff-plans/{id}                     — program detail + rules + assignment count
 *   POST /api/v1/spiff-plans                           — create draft
 *   PATCH /api/v1/spiff-plans/{id}                    — update draft fields
 *   POST /api/v1/spiff-plans/{id}/submit              — submit for approval
 *   POST /api/v1/spiff-plans/{id}/approve             — EP66 standalone CA-2 approval
 *   POST /api/v1/spiff-plans/{id}/reject              — return for changes
 *   POST /api/v1/spiff-plans/{id}/withdraw-submission — submitter self-withdraw
 *
 * Supplies to SpiffProgramWorkspace:
 *   program       — normalised spiff_plan record
 *   loading       — bool
 *   error         — string|null
 *   saving        — bool
 *   saveError     — string|null
 *   load()        — reload from server
 *   create(body)  — POST new program, resolves with { plan_id }
 *   patch(fields) — PATCH draft fields
 *   submit()      — submit for CA-2 approval
 *   approve()     — EP66 standalone CA-2 approve
 *   reject()      — return for changes
 *   withdraw()    — submitter self-withdraw
 *
 * @author data-fit LLC · DecisionFlow · S924
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = '/api/v1';

function _iso(v) {
  return v ?? null;
}

function normalizeProgram(raw) {
  if (!raw) return null;
  const p = raw.spiff_plan ?? raw;
  return {
    planId:                   p.plan_id,
    planName:                 p.plan_name ?? '',
    planDescription:          p.plan_description ?? null,
    spiffPlanCode:            p.spiff_plan_code ?? null,
    triggerModel:             p.trigger_model ?? null,
    effectiveFrom:            _iso(p.effective_from),
    effectiveTo:              _iso(p.effective_to),
    payoutCap:                p.payout_cap ?? null,
    payoutCurrency:           p.payout_currency ?? null,
    planComponentId:          p.plan_component_id ?? null,
    isActive:                 p.is_active ?? true,
    planStatus:               p.plan_status ?? 'draft',
    // Policy envelope — m_s923_01
    maxIndividualAward:       p.max_individual_award ?? null,
    maxDurationDays:          p.max_duration_days ?? null,
    financeApprovalThreshold: p.finance_approval_threshold ?? null,
    permittedPayoutForms:     p.permitted_payout_forms ?? [],
    // Version chain
    version:                  p.version ?? 1,
    supersededBy:             p.superseded_by ?? null,
    supersededAt:             _iso(p.superseded_at),
    // Approval actors
    submittedBy:              p.submitted_by ?? null,
    submittedAt:              _iso(p.submitted_at),
    approvedBy:               p.approved_by ?? null,
    approvedAt:               _iso(p.approved_at),
    createdBy:                p.created_by ?? null,
    createdAt:                _iso(p.created_at),
    updatedAt:                _iso(p.updated_at),
    // Rules (from GET detail)
    rules:                    p.rules ?? [],
    activeAssignmentCount:    p.active_assignment_count ?? 0,
  };
}

export default function useSpiffProgram(planId) {
  const [program,   setProgram]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);

  const mountedRef = useRef(true);
  const abortRef   = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------
  const load = useCallback(async (id = planId) => {
    if (!id) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    try {
      const r = await fetch(`${API_BASE}/spiff-plans/${id}`, {
        credentials: 'include',
        signal: ctrl.signal,
      });
      if (!mountedRef.current || ctrl.signal.aborted) return;
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error?.message || `Failed to load SPIFF plan (${r.status})`);
      }
      const data = await r.json();
      if (!mountedRef.current || ctrl.signal.aborted) return;
      setProgram(normalizeProgram(data));
    } catch (err) {
      if (!mountedRef.current || err.name === 'AbortError') return;
      setError(err.message || 'Failed to load SPIFF plan.');
    } finally {
      if (mountedRef.current && !ctrl.signal.aborted) setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    if (planId) load(planId);
    return () => { abortRef.current?.abort(); };
  }, [planId, load]);

  // ---------------------------------------------------------------------------
  // Mutation helper
  // ---------------------------------------------------------------------------
  async function _mutate(url, method = 'POST', body = null) {
    setSaving(true);
    setSaveError(null);
    try {
      const opts = {
        method,
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : {},
      };
      if (body) opts.body = JSON.stringify(body);
      const r = await fetch(url, opts);
      if (!mountedRef.current) return null;
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody?.error?.message || `Request failed (${r.status})`);
      }
      return await r.json();
    } catch (err) {
      if (mountedRef.current) setSaveError(err.message || 'Action failed.');
      throw err;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const create = useCallback(async (body) => {
    const data = await _mutate(`${API_BASE}/spiff-plans`, 'POST', body);
    if (data?.spiff_plan) setProgram(normalizeProgram(data));
    return data?.spiff_plan;
  }, []);

  const patch = useCallback(async (fields) => {
    if (!planId) return;
    const data = await _mutate(`${API_BASE}/spiff-plans/${planId}`, 'PATCH', fields);
    if (data?.spiff_plan) setProgram(normalizeProgram(data));
  }, [planId]);

  const submit = useCallback(async () => {
    if (!planId) return;
    await _mutate(`${API_BASE}/spiff-plans/${planId}/submit`, 'POST');
    await load(planId);
  }, [planId, load]);

  const approve = useCallback(async () => {
    if (!planId) return;
    await _mutate(`${API_BASE}/spiff-plans/${planId}/approve`, 'POST');
    await load(planId);
  }, [planId, load]);

  const reject = useCallback(async () => {
    if (!planId) return;
    await _mutate(`${API_BASE}/spiff-plans/${planId}/reject`, 'POST');
    await load(planId);
  }, [planId, load]);

  const withdraw = useCallback(async () => {
    if (!planId) return;
    await _mutate(`${API_BASE}/spiff-plans/${planId}/withdraw-submission`, 'POST');
    await load(planId);
  }, [planId, load]);

  return {
    program, loading, error,
    saving, saveError,
    load, create, patch,
    submit, approve, reject, withdraw,
  };
}
